import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  dispatchCompareKeyListUpdated,
  fetchComparePackageList,
  getCompareKeyList,
  removeAllKeysFromCompareKeyList,
  scrollToPreviousItem,
  updateAndFetchCompareKeyList,
} from './helpers';
import CUSTOM_EVENT from '../../../util/customEvents';
import FullscreenModal, { modalOpenClass } from '../FullscreenModal';
import { compareListLabels } from './consts';
import Icon from '../Atoms/Icon';
import Loading from '../Core/Loading';
import PackageComparisonModalContent from '../PackageComparisonModalContent';
import { compareItemButtonContainerClass } from '../CompareItemButton';

const propTypes = {
  labels: compareListLabels.isRequired,
  maximumCompareAmount: PropTypes.number.isRequired,
  baseApi: PropTypes.string.isRequired,
};

const paddingGapOffsetInPx = 26;

function CompareListComponent({ labels, maximumCompareAmount, baseApi }) {
  // The key list is for adding and removing packages to compare
  // and for tracking number of items pre-clicking the compare list.
  const [compareKeyList, setCompareKeyList] = useState([]);
  const [isCompareKeyListLoading, setIsCompareKeyListLoading] = useState(false);
  // The actual compare list data once the compare list button has been clicked.
  const [comparePackageList, setComparePackageList] = useState([]);
  const [isComparePackageListLoading, setIsComparePackageListLoading] = useState(false);

  const [isPackageComparisonModalOpen, setIsPackageComparisonModalOpen] = useState(false);

  const packageItemRefs = useRef([]);
  const contentRef = useRef(null);

  const handleClose = () => setIsPackageComparisonModalOpen(false);

  const isAtMaxComparisonAmount = compareKeyList.length >= maximumCompareAmount;

  // Get the initial compare key list for the compare badge and compare list buttons.
  // Also hold an event listener so on any compare button remount,
  // re-update the compare button selected/unselected state.
  useEffect(() => {
    const getCompareListPoll = async () => {
      const maxAttempts = 50; // Maximum number of polling attempts
      // Polling Duration: The total polling duration is maxAttempts * interval,
      // maxAttempts = 50 and interval = 100ms, the polling will last up to 5 seconds.
      let attempts = 0;

      const checkListenersReady = async () => {
        const compareButtons = document.querySelectorAll(`.${compareItemButtonContainerClass}`);
        if (compareButtons.length > 0) {
          await getCompareKeyList(
            setCompareKeyList,
            setIsCompareKeyListLoading,
            baseApi,
            maximumCompareAmount
          );
          return true;
        }
        return false;
      };

      const intervalId = setInterval(() => {
        checkListenersReady().then((isReady) => {
          attempts += 1;
          if (isReady || attempts >= maxAttempts) {
            clearInterval(intervalId);
          }
        });
      }, 100);

      return () => clearInterval(intervalId);
    };

    getCompareListPoll();

    window.addEventListener(CUSTOM_EVENT.GET_COMPARE_BUTTON_STATE_ON_REMOUNT, getCompareListPoll);

    return () => {
      window.removeEventListener(
        CUSTOM_EVENT.GET_COMPARE_BUTTON_STATE_ON_REMOUNT,
        getCompareListPoll
      );
    };
  }, [baseApi]);

  // Listen for the custom event dispatched by CompareButton that contain the updated key.
  // Respond by using the key to fetch the updated compare key list data.
  useEffect(() => {
    const handleCompareKeyListUpdate = (compareUpdateEvent) => {
      const { tourKey } = compareUpdateEvent.detail;
      if (compareKeyList.includes(tourKey)) {
        // Remove item
        updateAndFetchCompareKeyList(
          tourKey,
          setCompareKeyList,
          setIsCompareKeyListLoading,
          baseApi,
          labels
        );
      } else {
        // Add item
        if (isAtMaxComparisonAmount) {
          alert(labels.maximumCompareAmountReachedValidationMessage);
          setIsCompareKeyListLoading(false);
          // send same list back with no update so compare list item button finishes loading
          dispatchCompareKeyListUpdated(compareKeyList, true);
          return;
        }
        const isGoingToBeAtMaxComparisonAmount = compareKeyList.length + 1 >= maximumCompareAmount;
        updateAndFetchCompareKeyList(
          tourKey,
          setCompareKeyList,
          setIsCompareKeyListLoading,
          baseApi,
          labels,
          isGoingToBeAtMaxComparisonAmount
        );
      }
    };

    window.addEventListener(CUSTOM_EVENT.COMPARE_KEY_UPDATE, handleCompareKeyListUpdate);

    return () => {
      window.removeEventListener(CUSTOM_EVENT.COMPARE_KEY_UPDATE, handleCompareKeyListUpdate);
    };
  }, [compareKeyList, maximumCompareAmount, labels, baseApi]);

  useEffect(() => {
    if (comparePackageList.length === 0 && isPackageComparisonModalOpen) {
      document.body.classList.remove(modalOpenClass);
      setIsPackageComparisonModalOpen(false);
    }
  }, [comparePackageList]);

  const handleCompareListButtonClick = async () => {
    await fetchComparePackageList(baseApi, setComparePackageList, setIsComparePackageListLoading);
    document.body.classList.add(modalOpenClass);
    setIsPackageComparisonModalOpen(true);
  };

  const onRemovePackageItem = async (tourKey) => {
    try {
      await updateAndFetchCompareKeyList(
        tourKey,
        setCompareKeyList,
        setIsCompareKeyListLoading,
        baseApi,
        labels
      );
      await fetchComparePackageList(baseApi, setComparePackageList, setIsComparePackageListLoading);
      scrollToPreviousItem(
        packageItemRefs,
        comparePackageList,
        tourKey,
        contentRef,
        paddingGapOffsetInPx
      );
    } catch (error) {
      console.error('Error in onRemovePackageItem:', error);
    }
  };

  const onRemoveAllPackageItems = async () => {
    await removeAllKeysFromCompareKeyList(
      setCompareKeyList,
      setComparePackageList,
      baseApi,
      labels
    );
  };

  const compareListButtonIsLoading = isCompareKeyListLoading || isComparePackageListLoading;
  if (compareKeyList.length === 0) return null;

  const renderButtonContents = () => {
    if (compareListButtonIsLoading) {
      return (
        <div className="CompareList_button loading">
          <Loading />
        </div>
      );
    }

    return (
      <>
        <Icon id="compare" blockName="CompareList_icon" />
        <span>{labels.openModalCtaText}</span>
        {compareKeyList.length > 0 && (
          <div className="CompareList_badge">{compareKeyList.length}</div>
        )}
      </>
    );
  };

  return (
    <div className="CompareList_wrapper">
      <button
        className="CompareList_button"
        type="button"
        onClick={handleCompareListButtonClick}
        disabled={compareListButtonIsLoading}
      >
        {renderButtonContents()}
      </button>
      {comparePackageList?.length > 0 && (
        <FullscreenModal
          closeModalText={labels.closeModalCtaText}
          isOpen={isPackageComparisonModalOpen}
          onClose={handleClose}
          isFullBleed
          isPackageComparisonModal
        >
          <PackageComparisonModalContent
            comparePackageList={comparePackageList}
            maximumCompareAmount={maximumCompareAmount}
            labels={labels}
            onRemovePackageItem={onRemovePackageItem}
            onRemoveAllPackageItems={onRemoveAllPackageItems}
            isCompareKeyListLoading={isCompareKeyListLoading}
            packageItemRefs={packageItemRefs}
            contentRef={contentRef}
          />
        </FullscreenModal>
      )}
    </div>
  );
}

CompareListComponent.propTypes = propTypes;
export default CompareListComponent;
